// Extracted from ProductsPage.js. Multi-size
// "Add to Selection" dialog for Plywood / Blockboard / MDF / Doors /
// WPC-PVC per-sq-ft products, with stock info and inner cart.
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Plus, X } from 'lucide-react';
import { formatINR } from '../../../lib/store';
import { DOOR_SIZE_OPTIONS } from '../constants';
import { sortPlywoodSizes } from '../helpers';

export function PlywoodMultiSelectDialog({
  open,
  onClose,
  selectedGroup,
  product,
  thickness,
  sizeOptions,
  selectedSize,
  setSelectedSize,
  quantityInput,
  setQuantityInput,
  stockInfo,
  cart,
  onAddToCart,
  onRemoveFromCart,
  onMultiAdd,
  onSingleAdd,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="plywood-multi-size-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1A1C16]">Select Size & Add</DialogTitle>
        </DialogHeader>

        {selectedGroup && product && (
          <div className="space-y-4">
            {/* Product Info - Rate per sq ft prominent */}
            <div className="p-3 bg-[#556B2F]/10 rounded-lg border border-[#556B2F]/30">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-[#1A1C16]">{selectedGroup.brand}</p>
                  <p className="text-sm text-[#5C6150]">{selectedGroup.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-[#556B2F]">{formatINR(product.base_rate || 0)}</p>
                  <p className="text-xs text-[#5C6150]">per sq ft</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-xs bg-[#556B2F] text-white px-2 py-0.5 rounded font-medium">
                  {thickness}
                </span>
                {product.grade && (
                  <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{product.grade}</span>
                )}
              </div>
            </div>

            {/* All Sizes - Grouped by width (4ft row, then 3ft row) */}
            {(() => {
              // Doors are measured in inches so we use DOOR_SIZE_OPTIONS;
              // all other per-sq-ft categories use the standard feet sizes.
              const sourceSizeOptions = selectedGroup?.isDoor ? DOOR_SIZE_OPTIONS : sizeOptions;
              const productSizes = product.available_sizes || [];

              // Build the displayed size list. If the product has custom
              // available_sizes (e.g. Doors with non-standard "21x28"), use
              // those directly and derive sqft from the dimensions when
              // they aren't in the constant list — otherwise the buttons
              // would be empty (iter55 bug).
              let displayedSizes;
              if (productSizes.length > 0) {
                displayedSizes = productSizes.map(sizeVal => {
                  const fromConst = sourceSizeOptions.find(s => s.value === sizeVal);
                  if (fromConst) return fromConst;
                  // Derive sqft from numeric WxH ("21x28")
                  const parts = String(sizeVal).split('x').map(p => parseFloat(p));
                  let sqft = 32;
                  if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                    sqft = selectedGroup?.isDoor
                      ? (parts[0] * parts[1]) / 144  // inches → sqft
                      : parts[0] * parts[1];          // feet × feet
                  }
                  const label = selectedGroup?.isDoor
                    ? `${parts[0]}" x ${parts[1]}"`
                    : `${parts[0]} x ${parts[1]}`;
                  return { label, value: sizeVal, sqft };
                });
                // Also surface admin-configured custom size
                // presets (e.g. 8.5x4) here — they should always be a
                // one-click option regardless of which sizes are on the
                // product master. Doors excluded (they measure in inches).
                if (!selectedGroup?.isDoor) {
                  const shownValues = new Set(displayedSizes.map(s => (s.value || '').toLowerCase()));
                  const extras = sourceSizeOptions.filter(s => s.isCustom && !shownValues.has((s.value || '').toLowerCase()));
                  displayedSizes = [...displayedSizes, ...extras];
                }
              } else {
                displayedSizes = sourceSizeOptions;
              }

              const sortedSizeOptions = sortPlywoodSizes(displayedSizes);

              // Uniform grid for doors (many sizes), per-width grouped grid
              // for plywood/WPC etc. (small set of widths).
              const isDoorGrid = selectedGroup?.isDoor;
              if (isDoorGrid) {
                return (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                    {sortedSizeOptions.map((size) => {
                      const isSelected = selectedSize === size.value;
                      return (
                        <button
                          key={size.value}
                          onClick={() => setSelectedSize(size.value)}
                          className={`px-3 py-2 rounded-md border text-center transition-all ${
                            isSelected
                              ? 'border-[#556B2F] bg-[#556B2F] text-white'
                              : 'border-[#E2E6D9] bg-white hover:border-[#556B2F]/50'
                          }`}
                        >
                          <span className={`text-sm font-semibold whitespace-nowrap ${isSelected ? 'text-white' : 'text-[#1A1C16]'}`}>{size.label}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              }

              // Group by width (for plywood-style sizes "8x4", "6x4" etc.)
              const groupedByWidth = {};
              sortedSizeOptions.forEach(size => {
                const width = size.value.split('x')[1];
                if (!groupedByWidth[width]) groupedByWidth[width] = [];
                groupedByWidth[width].push(size);
              });
              // Sort widths descending (4ft first, then 3ft)
              const sortedWidths = Object.keys(groupedByWidth).sort((a, b) => parseFloat(b) - parseFloat(a));

              return (
                <div className="space-y-2">
                  {sortedWidths.map(width => (
                    <div key={width} className="flex flex-wrap gap-1.5">
                      {groupedByWidth[width].map((size) => {
                        const isSelected = selectedSize === size.value;
                        return (
                          <button
                            key={size.value}
                            onClick={() => setSelectedSize(size.value)}
                            className={`flex-1 min-w-[60px] px-3 py-2 rounded-md border text-center transition-all ${
                              isSelected
                                ? 'border-[#556B2F] bg-[#556B2F] text-white'
                                : 'border-[#E2E6D9] bg-white hover:border-[#556B2F]/50'
                            }`}
                          >
                            <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-[#1A1C16]'}`}>{size.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Quantity & Add to Selection */}
            {selectedSize && (
              <div className="space-y-2 pt-3 border-t border-[#E2E6D9]">
                {/* Stock Info Line */}
                {stockInfo && (
                  <div className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                    stockInfo.free_stock <= 0
                      ? 'bg-red-50 text-red-700'
                      : stockInfo.free_stock <= 20
                        ? 'bg-yellow-50 text-yellow-700'
                        : 'bg-green-50 text-green-700'
                  }`} data-testid="stock-info-line">
                    <span>Stock: <b>{stockInfo.physical_stock}</b></span>
                    <span className="text-[#8F9485]">|</span>
                    <span>Quoted: <b>{stockInfo.quoted_stock}</b></span>
                    <span className="text-[#8F9485]">|</span>
                    <span>Free: <b>{stockInfo.free_stock}</b></span>
                  </div>
                )}

                {/* Quantity Warning */}
                {stockInfo && parseInt(quantityInput || 0) > stockInfo.free_stock && (
                  <div className="text-xs px-2 py-1 bg-orange-50 text-orange-700 rounded flex items-center gap-1">
                    <span>⚠️ Only {stockInfo.free_stock} sheets free. You may need to source from supplier.</span>
                  </div>
                )}

                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <Label className="text-xs uppercase tracking-wide text-[#5C6150]">Quantity</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newQty = Math.max(1, (parseInt(quantityInput) || 1) - 1);
                          setQuantityInput(String(newQty));
                        }}
                        className="h-9 w-9"
                      >-</Button>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={quantityInput}
                        onChange={(e) => setQuantityInput(e.target.value.replace(/[^0-9]/g, ''))}
                        onBlur={() => {
                          if (!quantityInput || parseInt(quantityInput) < 1) {
                            setQuantityInput('1');
                          }
                        }}
                        className="text-center h-9 w-16 font-bold"
                        data-testid="plywood-quantity-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newQty = (parseInt(quantityInput) || 1) + 1;
                          setQuantityInput(String(newQty));
                        }}
                        className="h-9 w-9"
                      >+</Button>
                    </div>
                  </div>
                  <Button
                    onClick={onAddToCart}
                    className="bg-[#DAA520] hover:bg-[#B8860B] text-white h-10 px-5"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add to Selection
                  </Button>
                </div>
              </div>
            )}

            {/* Cart / Selection Summary */}
            {cart.length > 0 && (
              <div className="border-t border-[#E2E6D9] pt-3">
                <Label className="text-xs uppercase tracking-wide text-[#5C6150] mb-2 block">
                  Your Selection ({cart.length} items)
                </Label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {cart.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-[#F5F7F2] rounded">
                      <div>
                        <span className="text-sm font-medium">{item.sizeLabel}</span>
                        <span className="text-xs text-[#8F9485] ml-2">× {item.quantity}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono">{formatINR(item.rate * item.quantity)}</span>
                        <button
                          onClick={() => onRemoveFromCart(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#E2E6D9]">
                  <span className="text-sm font-semibold">Total:</span>
                  <span className="text-lg font-bold font-mono-data text-[#556B2F]">
                    {formatINR(cart.reduce((sum, item) => sum + item.rate * item.quantity, 0))}
                  </span>
                </div>
              </div>
            )}

            {/* Add to Quotation Button */}
            <div className="pt-2">
              {cart.length > 0 ? (
                <Button
                  onClick={onMultiAdd}
                  className="w-full bg-[#556B2F] hover:bg-[#465926] text-white uppercase tracking-wider font-semibold h-12"
                  data-testid="plywood-multi-add-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add {cart.reduce((sum, item) => sum + item.quantity, 0)} Sheet(s) to Quotation
                </Button>
              ) : selectedSize ? (
                <Button
                  onClick={onSingleAdd}
                  className="w-full bg-[#556B2F] hover:bg-[#465926] text-white uppercase tracking-wider font-semibold h-12"
                  data-testid="plywood-multi-add-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add to Quotation
                </Button>
              ) : (
                <p className="text-center text-sm text-[#8F9485]">Select a size to add</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
